import type { WeeklyGoal } from '../../services/shop-service';

/**
 * "This week's goal" — a shop's serve target and how far along it is.
 *
 * Shares ShopBeans' shell exactly (2px ink-trim wrapper around a white
 * pixel-corners card) because the two sit side by side in HOME's split row and
 * any difference in padding or trim reads as a misalignment.
 *
 * The counter is TOTAL STAMPS. `label` is the owner's free text — a shop may
 * write "cappuccinos" and the card will read "142 / 200 cappuccinos" — because
 * NFC and barista taps carry no drink identity, so a genuine per-item count is
 * impossible outside Square-connected shops. The label reads specific; the
 * number stays honest.
 */
export default function ShopGoal({ goal, served }: { goal?: WeeklyGoal | null; served: number }) {
  // A disabled goal and a zero target render nothing rather than an empty bar —
  // and the zero check also keeps the percentage below from dividing by zero.
  if (!goal?.enabled || goal.target <= 0) return null;

  const pct = Math.min(100, Math.round((served / goal.target) * 100));
  const hit = served >= goal.target;

  return (
    <div className="w-full pixel-corners bg-primary p-[2px]">
      <div className="w-full h-full bg-white pixel-corners p-[12px]">
        <p className="font-heading text-[8px] text-body-grey tracking-[1px] mb-2 text-center">
          THIS WEEK'S GOAL
        </p>

        <p className="font-grotesk font-bold text-[22px] leading-none text-primary text-center">
          {served}
          <span className="text-body-grey text-[15px]"> / {goal.target}</span>
        </p>

        {goal.label && (
          <p className="font-grotesk font-medium text-[11px] text-body-grey text-center mt-1 leading-tight">
            {goal.label}
          </p>
        )}

        <div className="mt-2.5 h-[10px] w-full bg-tab-inactive pixel-corners-sm overflow-hidden">
          <div
            data-testid="goal-bar"
            className={`h-full ${hit ? 'bg-coral' : 'bg-pixel-cyan'}`}
            style={{ width: `${pct}%` }}
          />
        </div>

        {hit && (
          <p className="font-heading text-[7px] text-coral text-center mt-2">GOAL SMASHED</p>
        )}
      </div>
    </div>
  );
}
